import { userService } from '@services/api';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';

/**
 * Returns the current user's availability via `userService.getMyAvailability`.
 *
 * This is the authoritative self-availability signal — `UserSummary.available`
 * from `lookupUsers` doesn't consistently surface self-availability, so the
 * Workforce Console uses this hook to drive the "Assigned to me" online dot
 * and to ensure the current user counts toward the ONLINE KPI tile.
 *
 * 60-second `staleTime` + `refetchInterval` keeps the dot fresh enough for
 * the manager's "is this person around right now" question without burning
 * traffic — availability state changes on the order of minutes, not seconds.
 */
export const useMyAvailability = () => {
  const userId = useStore(state => state.user?.id ?? '');

  const query = useQuery({
    queryKey: ['compass', 'auth', 'myAvailability', userId],
    queryFn: () => userService.getMyAvailability({}),
    enabled: !!userId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    gcTime: 5 * 60 * 1000,
  });

  return {
    isAvailable: query.data?.available ?? false,
    isLoading: query.isLoading,
  };
};
